/**
 * Family connections — the pieces the patient tab and the workspace share:
 * the query keys, the status chip, one row's facts and actions, and the
 * three dialogs (invite, decline, accept-with-explicit-patient). Every
 * write invalidates the whole family cache — the panel, the workspace and
 * the nav badge all read the same projection.
 */

import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Machine, Notice, TextField } from '@/ui/kit';
import { ConfirmDialog, SelectField, TextAreaField } from '@/ui/formkit';
import { ApiError, NetworkError } from '@/core/api/errors';
import {
  FAMILY_DECLINE_REASONS,
  acceptFamilyRequest,
  cancelFamilyInvitation,
  declineFamilyRequest,
  endFamilyConnection,
  inviteFamily,
  resendFamilyInvitation,
  type FamilyDeclineReason,
  type FamilyLinkView,
} from '@/core/clinic/familyApi';
import {
  listPatients,
  lookupPatientByMobile,
  type DuplicateCandidate,
  type PatientView,
} from '@/core/clinic/patientsApi';
import { useDebounced } from '@/core/api/useDebounced';
import { useLocale } from '@/core/i18n/LocaleProvider';
import { formatDate, formatTime } from '@/core/calendar/format';
import { formatWait } from '../access/otpThrottle';
import { useClinicSettings } from '../patients/useClinicSettings';
import { useStaffNames } from '../assessments/useStaffNames';
import { recordNumberFromInput } from '../patients/lifecycle';
import { mergeById, nameSearchSpecs } from '../patients/nameSearch';
import type { QueryFilter } from '@/core/api/querySpec';
import {
  familyErrorKey,
  inviteCandidates,
  linkActions,
  statusLabelKey,
  statusTone,
} from './familyRules';

/** One prefix for everything family: invalidate it after any write. */
export const familyKey = {
  all: ['family'] as const,
  panel: (patientId: string) => ['family', 'panel', patientId] as const,
  links: (clinicId: string | null) => ['family', 'links', clinicId] as const,
  requests: (clinicId: string | null) => ['family', 'requests', clinicId] as const,
};

export function useInvalidateFamily() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: familyKey.all });
}

/** The operator-facing sentence for a refused family action, with the wait when there is one. */
export function useFamilyErrorText() {
  const intl = useIntl();
  return (err: unknown): string => {
    if (err instanceof NetworkError) return intl.formatMessage({ id: 'signin.netBody' });
    if (!(err instanceof ApiError)) return intl.formatMessage({ id: 'family.err.generic' });
    const text = intl.formatMessage({ id: familyErrorKey(err) });
    return err.retryAfterSeconds !== undefined
      ? `${text} ${intl.formatMessage({ id: 'family.err.wait' }, { wait: formatWait(intl, err.retryAfterSeconds) })}`
      : text;
  };
}

export function useFamilyFormat() {
  const { locale } = useLocale();
  const settings = useClinicSettings();
  const cal = settings?.calendarSystem ?? 'jalali';
  const tz = settings?.timezone ?? 'Asia/Tehran';
  return {
    date: (iso: string | null | undefined) =>
      iso ? formatDate(iso, { locale, calendar: cal, timezone: tz }) : '—',
    dateTime: (iso: string | null | undefined) =>
      iso
        ? `${formatDate(iso, { locale, calendar: cal, timezone: tz })} ${formatTime(iso, { locale, timezone: tz })}`
        : '—',
  };
}

export function StatusChip({ link }: { link: FamilyLinkView }) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const label =
    link.localState === 'creating'
      ? t('family.local.creating')
      : link.localState === 'failed'
        ? t('family.local.failed')
        : intl.messages[statusLabelKey(link.status)]
          ? t(statusLabelKey(link.status))
          : link.status;
  return <Chip kind={statusTone(link.status, link.localState)}>{label}</Chip>;
}

export function KindLabel({ kind }: { kind: FamilyLinkView['kind'] }) {
  const intl = useIntl();
  return <>{intl.formatMessage({ id: `family.kind.${kind}` })}</>;
}

/**
 * The one-line story of a row: who it is with, what happened, when. Nothing
 * here is inferred — every fact is a projection field.
 */
export function LinkFacts({ link }: { link: FamilyLinkView }) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) =>
    intl.formatMessage({ id }, v) as string;
  const f = useFamilyFormat();
  const { nameOf } = useStaffNames();
  const lines: React.ReactNode[] = [];
  if (link.kind === 'invitation' && link.mobileUsed)
    lines.push(
      <span key="to">
        {t('family.row.to', { mobile: '' })}
        <Machine>{link.mobileUsed}</Machine>
      </span>,
    );
  if (link.kind === 'request')
    lines.push(
      <span key="from">
        {link.requesterDisplayName
          ? t('family.row.from', { name: link.requesterDisplayName, mobile: '' })
          : t('family.row.fromUnknown')}
        {link.requesterMobile && <Machine>{link.requesterMobile}</Machine>}
      </span>,
    );
  if (link.message)
    lines.push(<span key="msg">{t('family.row.message', { text: link.message })}</span>);
  if (link.note) lines.push(<span key="note">{t('family.row.note', { text: link.note })}</span>);
  if (link.kind === 'invitation' && link.status === 'pending' && link.localState === 'synced') {
    lines.push(
      <span key="sent">
        {t('family.row.sent', { n: link.sendCount, when: f.dateTime(link.lastSentAt) })}
      </span>,
    );
    if (link.expiresAt)
      lines.push(
        <span key="exp">{t('family.row.expires', { when: f.date(link.expiresAt) })}</span>,
      );
  }
  if (link.status === 'declined') {
    const reasonKey = `family.reason.${link.declineReason ?? ''}`;
    lines.push(
      <span key="why">
        {t('family.row.declineReason', {
          reason: intl.messages[reasonKey] ? t(reasonKey) : (link.declineReason ?? '—'),
        })}
        {link.declineNote && <> {t('family.row.declineNote', { note: link.declineNote })}</>}
      </span>,
    );
  }
  if (link.kind === 'connection' && link.status === 'accepted')
    lines.push(
      <span key="acc">{t('family.row.accepted', { when: f.dateTime(link.acceptedAt) })}</span>,
    );
  if (link.kind === 'connection' && link.status === 'ended') {
    const byKey = `family.endedBy.${link.endedReason ?? ''}`;
    lines.push(
      <span key="end">
        {t('family.row.ended', {
          when: f.dateTime(link.endedAt),
          by: intl.messages[byKey] ? t(byKey) : (link.endedReason ?? '—'),
        })}
      </span>,
    );
  }
  if (link.createdBy && link.kind !== 'request')
    lines.push(<span key="by">{t('family.row.by', { name: nameOf(link.createdBy) })}</span>);
  if (link.localState === 'failed')
    lines.push(
      <span key="failed" style={{ color: 'var(--zc-err-ink)' }}>
        {t('family.row.failed')}
      </span>,
    );
  return (
    <span
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '2px 12px',
        fontSize: 12.5,
        color: 'var(--zc-muted)',
      }}
    >
      {lines}
    </span>
  );
}

/**
 * A row's actions under families.manage. A 429 on resend keeps the button
 * inert for the wait Auth stated (shown in minutes), then offers it again;
 * nothing here invents a cooldown.
 */
export function LinkActionsBar({
  link,
  canManage,
  onAccept,
  onDecline,
  onNotice,
}: {
  link: FamilyLinkView;
  canManage: boolean;
  onAccept?: (link: FamilyLinkView) => void;
  onDecline?: (link: FamilyLinkView) => void;
  onNotice: (n: { kind: 'ok' | 'err'; text: string }) => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) =>
    intl.formatMessage({ id }, v) as string;
  const invalidate = useInvalidateFamily();
  const errorText = useFamilyErrorText();
  const acts = linkActions(link, canManage);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [resendUntil, setResendUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (resendUntil === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [resendUntil]);
  const waitLeft = resendUntil === null ? 0 : Math.max(0, Math.ceil((resendUntil - now) / 1000));

  const handlers = (okKey: string) => ({
    onSuccess: () => {
      onNotice({ kind: 'ok' as const, text: t(okKey) });
      void invalidate();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 429 && err.retryAfterSeconds !== undefined)
        setResendUntil(Date.now() + err.retryAfterSeconds * 1000);
      onNotice({ kind: 'err', text: errorText(err) });
    },
  });
  const resend = useMutationLike(
    () => resendFamilyInvitation(link.id),
    handlers('family.act.resent'),
  );
  const cancel = useMutationLike(
    () => cancelFamilyInvitation(link.id),
    handlers('family.act.cancelled'),
  );
  const end = useMutationLike(() => endFamilyConnection(link.id), handlers('family.end.done'));

  if (!acts.resend && !acts.cancel && !acts.end && !acts.accept && !acts.decline) return null;
  return (
    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {acts.resend && (
        <Button
          variant="quiet"
          disabled={resend.busy || waitLeft > 0}
          busy={resend.busy}
          onClick={resend.fire}
          title={
            waitLeft > 0
              ? t('family.act.resendWait', { wait: formatWait(intl, waitLeft) })
              : undefined
          }
        >
          {waitLeft > 0
            ? t('family.act.resendWait', { wait: formatWait(intl, waitLeft) })
            : t('family.act.resend')}
        </Button>
      )}
      {acts.cancel && (
        <Button variant="quiet" disabled={cancel.busy} busy={cancel.busy} onClick={cancel.fire}>
          {t('family.act.cancel')}
        </Button>
      )}
      {acts.accept && onAccept && (
        <Button variant="primary" onClick={() => onAccept(link)}>
          {t('family.act.accept')}
        </Button>
      )}
      {acts.decline && onDecline && (
        <Button variant="quiet" onClick={() => onDecline(link)}>
          {t('family.act.decline')}
        </Button>
      )}
      {acts.end && (
        <>
          <Button variant="quiet" onClick={() => setConfirmEnd(true)}>
            {t('family.act.end')}
          </Button>
          <ConfirmDialog
            open={confirmEnd}
            title={t('family.end.title')}
            body={t('family.end.body')}
            effectTitle={t('family.end.effectTitle')}
            effect={t('family.end.effect')}
            confirmLabel={t('family.end.confirm')}
            danger
            busy={end.busy}
            onConfirm={() => {
              setConfirmEnd(false);
              end.fire();
            }}
            onClose={() => setConfirmEnd(false)}
          />
        </>
      )}
    </span>
  );
}

/** A tiny mutation wrapper so each action keeps its own busy flag without a mutation per hook rule. */
function useMutationLike(
  fn: () => Promise<unknown>,
  handlers: { onSuccess: () => void; onError: (err: unknown) => void },
) {
  const m = useMutation({
    mutationFn: fn,
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
  return { fire: () => m.mutate(), busy: m.isPending };
}

/* ------------------------------ invite dialog ------------------------------ */

export function InviteDialog({
  patient,
  open,
  onClose,
  onNotice,
}: {
  patient: PatientView;
  open: boolean;
  onClose: () => void;
  onNotice: (n: { kind: 'ok' | 'err'; text: string }) => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) =>
    intl.formatMessage({ id }, v) as string;
  const invalidate = useInvalidateFamily();
  const errorText = useFamilyErrorText();
  const contactLabel = t('family.invite.contactLabel');
  const candidates = useMemo(
    () => inviteCandidates(patient, contactLabel),
    [patient, contactLabel],
  );
  const [mobile, setMobile] = useState(candidates[0]?.mobile ?? '');
  const [note, setNote] = useState('');
  const send = useMutation({
    mutationFn: () => inviteFamily(patient.id, { mobile, note: note.trim() || undefined }),
    onSuccess: () => {
      onNotice({ kind: 'ok', text: `${t('family.invite.sent')} — ${t('family.invite.sentBody')}` });
      void invalidate();
      setNote('');
      onClose();
    },
    onError: (err) => onNotice({ kind: 'err', text: errorText(err) }),
  });
  return (
    <ConfirmDialog
      open={open}
      title={t('family.invite.title')}
      body={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span>{t('family.invite.body')}</span>
          {candidates.length === 0 ? (
            <Notice kind="info">{t('family.invite.noNumbers')}</Notice>
          ) : (
            <SelectField
              label={t('family.invite.mobile')}
              value={mobile}
              onChange={setMobile}
              options={candidates.map((c) => ({
                value: c.mobile,
                label: `${c.label} · ${c.mobile}`,
              }))}
            />
          )}
          <TextAreaField
            label={t('family.invite.note')}
            value={note}
            onChange={setNote}
            help={t('family.invite.noteHelp')}
            rows={2}
          />
        </div>
      }
      confirmLabel={t('family.invite.send')}
      confirmDisabled={mobile === '' || send.isPending}
      busy={send.isPending}
      onConfirm={() => send.mutate()}
      onClose={onClose}
    />
  );
}

/* ------------------------------ decline dialog ------------------------------ */

export function DeclineDialog({
  link,
  onClose,
  onNotice,
}: {
  link: FamilyLinkView | null;
  onClose: () => void;
  onNotice: (n: { kind: 'ok' | 'err'; text: string }) => void;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id }) as string;
  const invalidate = useInvalidateFamily();
  const errorText = useFamilyErrorText();
  const [reason, setReason] = useState<FamilyDeclineReason>('not_now');
  const [note, setNote] = useState('');
  const decline = useMutation({
    mutationFn: () => declineFamilyRequest(link!.id, { reason, note: note.trim() || undefined }),
    onSuccess: () => {
      onNotice({ kind: 'ok', text: t('family.decline.done') });
      void invalidate();
      setNote('');
      onClose();
    },
    onError: (err) => onNotice({ kind: 'err', text: errorText(err) }),
  });
  return (
    <ConfirmDialog
      open={link !== null}
      title={t('family.decline.title')}
      body={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span>{t('family.decline.body')}</span>
          <SelectField
            label={t('family.decline.reason')}
            value={reason}
            onChange={(v) => setReason(v as FamilyDeclineReason)}
            options={FAMILY_DECLINE_REASONS.map((r) => ({
              value: r,
              label: t(`family.reason.${r}`),
            }))}
          />
          <TextAreaField
            label={t('family.decline.note')}
            value={note}
            onChange={setNote}
            rows={2}
          />
        </div>
      }
      confirmLabel={t('family.decline.confirm')}
      danger
      busy={decline.isPending}
      onConfirm={() => decline.mutate()}
      onClose={onClose}
    />
  );
}

/* ------------------------- accept: explicit patient ------------------------- */

interface PatientPick {
  id: string;
  firstName: string;
  lastName: string;
  recordNumber: number | null;
  registered: boolean;
}

const fromCandidate = (c: DuplicateCandidate): PatientPick => ({
  id: c.patientId,
  firstName: c.firstName,
  lastName: c.lastName,
  recordNumber: c.recordNumber,
  registered: c.registrationState === 'registered',
});
const fromView = (p: PatientView): PatientPick => ({
  id: p.id,
  firstName: p.firstName,
  lastName: p.lastName,
  recordNumber: p.recordNumber,
  registered: p.registrationState === 'registered',
});

/**
 * Accepting a request means choosing the patient yourself. The requester's
 * verified number finds the records that hold it (the same lookup the booking
 * desk uses); a search by name or file number covers the rest. Only a
 * registered record can be chosen, and nothing is chosen until you choose it.
 */
/** What accepting needs of a request: its id and what the family disclosed (ZOY-712 reuses it from a follow-up). */
export type AcceptTarget = Pick<FamilyLinkView, 'id' | 'requesterDisplayName' | 'requesterMobile'>;

export function AcceptDialog({
  link,
  onClose,
  onNotice,
}: {
  link: AcceptTarget | null;
  onClose: () => void;
  onNotice: (n: { kind: 'ok' | 'err'; text: string }) => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) =>
    intl.formatMessage({ id }, v) as string;
  const invalidate = useInvalidateFamily();
  const errorText = useFamilyErrorText();
  const [chosen, setChosen] = useState<PatientPick | null>(null);
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, 300);
  useEffect(() => {
    setChosen(null);
    setTerm('');
  }, [link?.id]);

  const byMobile = useQuery({
    queryKey: ['family', 'accept-lookup', link?.requesterMobile ?? ''],
    queryFn: () => lookupPatientByMobile(link!.requesterMobile!),
    enabled: link !== null && Boolean(link.requesterMobile),
    staleTime: 15_000,
  });
  const recordN = recordNumberFromInput(debounced);
  const search = useQuery({
    queryKey: ['family', 'accept-search', debounced],
    queryFn: async () => {
      // Only a registered record can be connected — Guests are listed by the
      // mobile lookup above with their reason, never offered here.
      const base: QueryFilter[] = [{ field: 'registeredAt', op: 'isnull', value: false }];
      const sort = [{ field: 'lastName', dir: 'asc' as const }];
      if (recordN !== null) {
        return listPatients({
          filters: [...base, { field: 'recordNumber', op: 'eq', value: recordN }],
          limit: 8,
          sort,
        });
      }
      // A name matches FIRST name as well as last, and "first last" as a
      // pair — the same rule as the booking desk (ZOY-336); the DSL has no
      // cross-field OR, so the bounded result sets merge client-side.
      const results = await Promise.all(
        nameSearchSpecs(debounced, base).map((filters) =>
          listPatients({ filters, limit: 8, sort }),
        ),
      );
      return { items: mergeById(results.map((r) => r.items)) };
    },
    enabled: link !== null && debounced.trim().length >= 2,
    staleTime: 15_000,
  });

  const accept = useMutation({
    mutationFn: () => acceptFamilyRequest(link!.id, chosen!.id),
    onSuccess: () => {
      onNotice({ kind: 'ok', text: t('family.accept.done') });
      void invalidate();
      onClose();
    },
    onError: (err) => onNotice({ kind: 'err', text: errorText(err) }),
  });

  const pickRow = (p: PatientPick) => (
    <div key={p.id} className={a.row} style={{ paddingInline: 0, minBlockSize: 0 }}>
      <span className={a.rowValue}>
        <span dir="auto">
          {p.firstName} {p.lastName}
        </span>
        {p.recordNumber !== null && (
          <>
            {' · '}
            <Machine>
              {t('family.accept.file', {
                n: intl.formatNumber(p.recordNumber, { useGrouping: false }),
              })}
            </Machine>
          </>
        )}
        {!p.registered && <span className={a.rowHint}>{t('family.accept.guestSkipped')}</span>}
      </span>
      <span className={a.rowAction}>
        {p.registered ? (
          chosen?.id === p.id ? (
            <Chip kind="ok">{t('family.accept.picked')}</Chip>
          ) : (
            <Button variant="quiet" onClick={() => setChosen(p)}>
              {t('family.accept.pick')}
            </Button>
          )
        ) : null}
      </span>
    </div>
  );

  const mobileHits = (byMobile.data?.candidates ?? []).map(fromCandidate);
  const searchHits = (search.data?.items ?? []).map(fromView);

  return (
    <ConfirmDialog
      open={link !== null}
      title={t('family.accept.title')}
      body={
        link && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span>{t('family.accept.body')}</span>
            <strong style={{ fontSize: 13 }}>
              {t('family.accept.requester', {
                name: link.requesterDisplayName ?? '—',
                mobile: link.requesterMobile ?? '—',
              })}
            </strong>
            {link.requesterMobile && (
              <section aria-label={t('family.accept.byMobile')}>
                <div style={{ fontSize: 12.5, color: 'var(--zc-muted)' }}>
                  {t('family.accept.byMobile')}
                </div>
                {byMobile.isPending ? (
                  <span style={{ fontSize: 12.5 }}>…</span>
                ) : mobileHits.length === 0 ? (
                  <span style={{ fontSize: 12.5 }}>{t('family.accept.byMobileNone')}</span>
                ) : (
                  mobileHits.map(pickRow)
                )}
              </section>
            )}
            <TextField
              label={t('family.accept.search')}
              value={term}
              onChange={setTerm}
              help={t('family.accept.searchHelp')}
            />
            {search.isFetched && searchHits.length === 0 && debounced.trim().length >= 2 && (
              <span style={{ fontSize: 12.5 }}>{t('family.accept.searchNone')}</span>
            )}
            {searchHits.map(pickRow)}
            {chosen === null && (
              <span style={{ fontSize: 12.5, color: 'var(--zc-muted)' }}>
                {t('family.accept.needsChoice')}
              </span>
            )}
          </div>
        )
      }
      confirmLabel={t('family.accept.confirm')}
      confirmDisabled={chosen === null || accept.isPending}
      busy={accept.isPending}
      onConfirm={() => accept.mutate()}
      onClose={onClose}
    />
  );
}
